<template>
	<n-modal
		v-model:show="show"
		:auto-focus="false"
		:trap-focus="false"
		:mask-closable="true"
		:block-scroll="true"
		class="pc-search-modal"
		@after-enter="handleAfterEnter"
		@after-leave="handleAfterLeave"
		@mask-click="handleMaskClick"
	>
		<div
			class="pc-search-modal__panel"
			role="dialog"
			aria-modal="true"
			:aria-label="t('common.pcSearchModal.ariaLabel')"
			@click.stop
		>
			<div class="search-bar">
				<img class="search-icon" :src="iconSearch" alt="" aria-hidden="true" />

				<input
					ref="inputRef"
					v-model="keyword"
					type="search"
					class="search-input"
					:placeholder="t('common.pcSearchModal.placeholder')"
					:disabled="searching"
					@focus="handleInputFocus"
					@keydown.enter="handleSearch"
				/>

				<button
					v-if="keyword"
					type="button"
					class="search-clear"
					:aria-label="t('common.pcSearchModal.clear')"
					@click="clearKeyword"
				>
					×
				</button>
				<span class="search-divider" aria-hidden="true"></span>
				<button type="button" class="search-btn" :disabled="searching" @click="handleSearch">
					{{ t("common.pcSearchModal.search") }}
				</button>
			</div>

			<template v-if="!searchMode">
				<section class="hot-section">
					<h3 class="section-title">{{ t("common.pcSearchModal.hotTitle") }}</h3>
					<div class="hot-tags">
						<button
							v-for="(tag, index) in hotTags"
							:key="tag"
							type="button"
							class="hot-tag"
							@click="handleHotTagClick(tag, index)"
						>
							{{ tag }}
						</button>
					</div>
				</section>

				<section class="teacher-section">
					<h3 class="section-title">{{ t("common.pcSearchModal.hotTeachersTitle") }}</h3>
					<div v-if="hotLoading" class="state-text">{{ t("common.pcSearchModal.loading") }}</div>
					<ul v-else-if="hotTeachers.length" class="teacher-list">
						<li
							v-for="teacher in hotTeachers"
							:key="teacher.id"
							:ref="el => bindHotMentorImpression(el, teacher)"
							class="teacher-item"
							role="button"
							tabindex="0"
							@click="handleTeacherClick(teacher)"
							@keydown.enter="handleTeacherClick(teacher)"
						>
							<span class="teacher-rank">{{ teacher.rank }}</span>
							<img
								class="teacher-avatar"
								:src="teacher.avatar"
								:alt="teacher.name"
								loading="lazy"
								decoding="async"
							/>
							<div class="teacher-info">
								<div class="teacher-meta-row">
									<div class="teacher-name">{{ teacher.name }}</div>
									<div class="teacher-rating">
										<img :src="starIcon" alt="" aria-hidden="true" />
										<span>
											{{ teacher.rating }}({{
												t("common.pcSearchModal.consultCount", { count: teacher.consultCount })
											}})
										</span>
									</div>
								</div>
								<div v-if="teacher.tags.length" class="teacher-tags">
									{{ teacher.tags.join("·") }}
								</div>
							</div>
						</li>
					</ul>
					<div v-else class="state-text">{{ t("common.pcSearchModal.noHotTeachers") }}</div>
				</section>
			</template>

			<template v-else>
				<div v-if="visibleSearchTabs.length" class="search-tabs">
					<button
						v-for="tab in visibleSearchTabs"
						:key="tab.type"
						type="button"
						class="search-tab"
						:class="{ 'search-tab--active': activeSearchType === tab.type }"
						@click="switchSearchTab(tab.type)"
					>
						{{ tab.label }}
					</button>
				</div>

				<section
					ref="searchResultRef"
					class="search-result-section"
					@scroll="handleSearchResultScroll"
				>
					<div v-if="searching" class="state-text">{{ t("common.pcSearchModal.searching") }}</div>

					<template v-else-if="searchResults.length">
						<ul v-if="activeSearchType === '0'" class="teacher-list">
							<li
								v-for="(item, index) in searchResults"
								:key="item.uid || index"
								:ref="el => bindSearchResultImpression(el, item, index)"
								class="teacher-item"
								role="button"
								tabindex="0"
								@click="handleTeacherResultClick(item, index)"
								@keydown.enter="handleTeacherResultClick(item, index)"
							>
								<span class="teacher-rank">{{ index + 1 }}</span>
								<img
									class="teacher-avatar"
									:src="item.avatar"
									:alt="item.nickname"
									loading="lazy"
									decoding="async"
								/>
								<div class="teacher-info">
									<div class="teacher-meta-row">
										<div class="teacher-name">{{ item.nickname }}</div>
										<div class="teacher-rating">
											<img :src="starIcon" alt="" aria-hidden="true" />
											<span>
												{{ formatScore(item.score) }}({{
													t("common.pcSearchModal.consultCount", { count: item.answers || 0 })
												}})
											</span>
										</div>
									</div>
									<div v-if="getTeacherSubtitle(item)" class="teacher-tags">
										{{ getTeacherSubtitle(item) }}
									</div>
									<div v-if="item.discount_tag" class="result-tag">{{ item.discount_tag }}</div>
								</div>
							</li>
						</ul>

						<ul v-else-if="activeSearchType === '1'" class="result-list">
							<li
								v-for="(item, index) in searchResults"
								:key="`${item.service_id}-${item.answer_id}`"
								:ref="el => bindSearchResultImpression(el, item, index)"
								class="result-item result-item--service"
								role="button"
								tabindex="0"
								@click="handleServiceClick(item, index)"
								@keydown.enter="handleServiceClick(item, index)"
							>
								<img
									class="result-item__avatar"
									:src="item.teacher_info?.avatar"
									:alt="item.teacher_info?.nickname"
									loading="lazy"
									decoding="async"
								/>
								<div class="result-item__content">
									<div class="result-item__title">{{ item.name }}</div>
									<div v-if="item.description" class="result-item__desc">
										{{ item.description }}
									</div>
									<div class="result-item__meta">
										<span>{{ item.teacher_info?.nickname }}</span>
										<span class="result-item__price">${{ item.price }}</span>
									</div>
									<div v-if="item.discount_tag" class="result-tag">{{ item.discount_tag }}</div>
								</div>
							</li>
						</ul>

						<PcSearchDynamicList
							v-else-if="activeSearchType === '2'"
							:items="searchResults"
							:keyword-type="currentKeywordType"
							@navigate="handleDynamicNavigate"
						/>

						<ul v-else class="result-list">
							<li
								v-for="(item, index) in searchResults"
								:key="item.id"
								:ref="el => bindSearchResultImpression(el, item, index)"
								class="result-item result-item--expert"
								@click="handleExpertNavigate(item, index)"
							>
								<img
									class="result-item__avatar"
									:src="item.avatar"
									:alt="item.nickname"
									loading="lazy"
									decoding="async"
								/>
								<div class="result-item__content">
									<div class="result-item__title">{{ item.nickname }}</div>
									<div v-if="item.introduce" class="result-item__desc">{{ item.introduce }}</div>
								</div>
								<button
									type="button"
									class="follow-btn"
									:class="{ 'follow-btn--followed': isItemFollowed(item) }"
									:disabled="isFollowLoading(item.uid || item.id)"
									@click.stop="handleFollowExpert(item)"
								>
									{{
										isItemFollowed(item) ? t("square.detail.followed") : t("square.detail.follow")
									}}
								</button>
							</li>
						</ul>
						<div v-if="loadingMore" class="search-result-section__footer">
							{{ t("common.pcSearchModal.loading") }}
						</div>
					</template>

					<div v-else class="state-text">{{ t("common.pcSearchModal.emptyResults") }}</div>
				</section>
			</template>
		</div>
	</n-modal>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeUnmount, provide, ref, watch } from "vue";
import { useThrottleFn } from "@vueuse/core";
import { getHomeFameTeacher, searchTemp, trackSquare } from "~/api/api";
import { getSearchTeacherUid } from "~/utils/search-temp";
import { useI18n } from "~/composables/useI18n";
import showMessage from "~/utils/showMessage";
import {
	createGlobalSearchImpressionTracker,
	getGlobalSearchKeywordLength,
	getGlobalSearchMentorType,
	GLOBAL_SEARCH_CLOSE_TYPES,
	GLOBAL_SEARCH_RESULT_TYPES,
	mapSearchTabToResultType,
	resolveGlobalSearchKeywordType,
	resolveGlobalSearchPlaceholderType,
	useGlobalSearchTracking,
} from "~/composables/useSiteComponentTracking";
import {
	resolveMediaPageName,
	useMediaSquareTracking,
} from "~/features/page-variants/seer/square/composables/useMediaSquareTracking";
import iconSearch from "~/features/page-variants/seer/user/pc/components/MyOrder/images/icon_search.svg";
import starIcon from "@variants/index/images/pc/start.png";
import PcSearchDynamicList from "./pc-search-dynamic-list.vue";

const TEACHER_SIZE = 6;
const SEARCH_TYPES = ["0", "1", "2", "3"];

function createEmptyResultsByType() {
	return Object.fromEntries(SEARCH_TYPES.map(type => [type, []]));
}

function createPaginationByType() {
	return Object.fromEntries(SEARCH_TYPES.map(type => [type, { page: 1, finished: false }]));
}

const show = defineModel("show", { type: Boolean, default: false });

const props = defineProps({
	searchTrackingScope: {
		type: String,
		default: "",
	},
	triggerPage: {
		type: String,
		default: "",
	},
	triggerPosition: {
		type: String,
		default: "top_search",
	},
});

const route = useRoute();
const { $isMobile } = useNuxtApp();
const { t, tm } = useI18n();
const openLoginPopup = inject("openLoginPopup", null);

const { track, resetSession, resolveTriggerPosition } = useGlobalSearchTracking(
	$isMobile ? "mobile" : "pc",
	{
		triggerPage: computed(() => props.triggerPage),
		triggerPosition: computed(() => props.triggerPosition),
	}
);
const { bind: bindImpression, reset: resetImpressionTracker } =
	createGlobalSearchImpressionTracker(track);
provide("globalSearchBindImpression", bindImpression);

const keyword = ref("");
const inputRef = ref(null);
const searchResultRef = ref(null);
const searching = ref(false);
const loadingMore = ref(false);
const hotLoading = ref(false);
const searchMode = ref(false);
const activeSearchType = ref("0");
const hotTeachers = ref([]);
const searchResultsByType = ref(createEmptyResultsByType());
const searchPaginationByType = ref(createPaginationByType());
const followLoadingMap = ref({});
const keywordModified = ref(false);
const pendingCloseType = ref(GLOBAL_SEARCH_CLOSE_TYPES.UNKNOWN);
const submittedSearch = ref(false);
const inputFocusFired = ref(false);

const hotTags = computed(() => {
	const tags = tm("common.pcSearchModal.hotTags");
	return Array.isArray(tags) ? tags : [];
});

const currentKeywordType = computed(() =>
	resolveGlobalSearchKeywordType(keyword.value, hotTags.value)
);

const allSearchTabs = computed(() => [
	{ type: "0", label: t("common.pcSearchModal.tabs.teacher") },
	{ type: "1", label: t("common.pcSearchModal.tabs.service") },
	{ type: "2", label: t("common.pcSearchModal.tabs.dynamic") },
	{ type: "3", label: t("common.pcSearchModal.tabs.expert") },
]);

const visibleSearchTabs = computed(() =>
	allSearchTabs.value.filter(tab => (searchResultsByType.value[tab.type] || []).length > 0)
);

const searchResults = computed(() => searchResultsByType.value[activeSearchType.value] || []);

const totalResultCount = computed(() =>
	SEARCH_TYPES.reduce((sum, type) => sum + (searchResultsByType.value[type] || []).length, 0)
);

const getFollowState = data => data?.is_follow === true || data?.is_follow === 1;

function isItemFollowed(item) {
	return getFollowState(item);
}

function isFollowLoading(id) {
	return !!followLoadingMap.value[String(id)];
}

function checkLogin() {
	if (!import.meta.client) return false;
	if (!localStorage.getItem("token")) {
		openLoginPopup?.();
		return false;
	}
	return true;
}

function formatScore(value) {
	const score = Number(value);
	if (!score) return "5.0";
	return Math.min(score, 5).toFixed(1);
}

function normalizeTags(source, academicSector = []) {
	if (typeof source === "string") {
		const tags = source
			.split(/[·,，]/)
			.map(item => item.trim())
			.filter(Boolean)
			.slice(0, 2);
		if (tags.length) return tags;
	}
	if (Array.isArray(academicSector)) {
		return academicSector.filter(Boolean).slice(0, 2);
	}
	return [];
}

function truncateText(text, max = 28) {
	if (!text) return "";
	return text.length > max ? `${text.slice(0, max)}...` : text;
}

function getTeacherSubtitle(item) {
	if (item.year) {
		return t("common.pcSearchModal.yearsExperience", { year: item.year });
	}
	return truncateText(item.introduce, 24);
}

function formatTeacherItem(teacher, rank) {
	const answerId = teacher?.answer_id || teacher?.id || teacher?.teacher_uid || teacher?.uid || "";
	return {
		id: answerId || teacher?.uid || rank,
		answerId,
		uid: teacher?.uid || "",
		areaType: teacher?.area_type,
		name: teacher?.nickname || teacher?.name || t("common.pcSearchModal.defaultTeacherName"),
		avatar: teacher?.avatar || "",
		rating: formatScore(teacher?.score),
		consultCount: Number(teacher?.answers || teacher?.evaluate_num) || 0,
		tags: normalizeTags(teacher?.tags, teacher?.academic_sector),
		link: answerId ? `/seer/teacher-detail/${answerId}` : "/seer/master-fortune",
		rank,
	};
}

function getSearchResultId(item, tabType = activeSearchType.value) {
	if (tabType === "0") {
		const uid = getSearchTeacherUid(item);
		return uid ? String(uid) : undefined;
	}
	if (tabType === "1") {
		return item?.service_id != null ? String(item.service_id) : undefined;
	}
	if (tabType === "2") {
		return item?.id != null ? String(item.id) : undefined;
	}
	const uid = item?.uid || item?.id;
	return uid != null ? String(uid) : undefined;
}

function bindHotMentorImpression(el, teacher) {
	if (!el || !teacher?.id) return;
	const mentorId = teacher.answerId || teacher.uid || String(teacher.id);
	bindImpression(el, `hot_mentor_${mentorId}`, "global_search_hot_mentor_impression", {
		mentor_id: mentorId,
		mentor_type: getGlobalSearchMentorType(teacher.areaType),
		rank: teacher.rank,
		rating: teacher.rating,
	});
}

function bindSearchResultImpression(el, item, index) {
	if (!el || searchMode.value === false) return;
	const resultId = getSearchResultId(item);
	if (!resultId) return;
	const resultType = mapSearchTabToResultType(activeSearchType.value);
	bindImpression(el, `result_${resultType}_${resultId}`, "global_search_result_impression", {
		result_type: resultType,
		result_id: resultId,
		position: index + 1,
		keyword_type: currentKeywordType.value,
	});
}

function handleInputFocus() {
	if (inputFocusFired.value) return;
	inputFocusFired.value = true;
	track("global_search_input_focus", {
		placeholder_type: resolveGlobalSearchPlaceholderType(keyword.value),
		keyword_type: currentKeywordType.value,
	});
}

function handleMaskClick() {
	pendingCloseType.value = GLOBAL_SEARCH_CLOSE_TYPES.MASK;
}

function closeSearchModal(closeType = GLOBAL_SEARCH_CLOSE_TYPES.UNKNOWN) {
	pendingCloseType.value = closeType;
	show.value = false;
}

async function fetchHotTeachers() {
	if (hotLoading.value || hotTeachers.value.length) return;

	hotLoading.value = true;
	try {
		const res = await getHomeFameTeacher({
			page: 1,
			size: TEACHER_SIZE,
			v: 2,
		});
		hotTeachers.value = (res?.data?.list || [])
			.slice(0, TEACHER_SIZE)
			.map((teacher, index) => formatTeacherItem(teacher, index + 1));
	} catch (error) {
		console.error("加载热门老师失败:", error);
		hotTeachers.value = [];
	} finally {
		hotLoading.value = false;
	}
}

async function requestSearchType(type, page = 1) {
	const value = keyword.value.trim();
	if (!value) return [];

	const params = {
		type,
		page: String(page),
		key_word: value,
	};

	if (type === "2") {
		params.v = "1";
	}

	const res = await searchTemp(params);
	return Array.isArray(res?.data) ? res.data : [];
}

async function fetchAllSearchResults() {
	const value = keyword.value.trim();
	if (!value || searching.value) return;

	searching.value = true;
	searchResultsByType.value = createEmptyResultsByType();
	searchPaginationByType.value = createPaginationByType();

	try {
		const nextResults = createEmptyResultsByType();
		const nextPagination = createPaginationByType();

		for (const type of SEARCH_TYPES) {
			try {
				const list = await requestSearchType(type, 1);
				nextResults[type] = list;
				nextPagination[type] = {
					page: 1,
					finished: !list.length,
				};
			} catch (error) {
				console.error(`搜索类型 ${type} 失败:`, error);
				nextResults[type] = [];
				nextPagination[type] = { page: 1, finished: true };
			}
		}

		searchResultsByType.value = nextResults;
		searchPaginationByType.value = nextPagination;

		const firstVisibleTab = allSearchTabs.value.find(tab => nextResults[tab.type]?.length);
		activeSearchType.value = firstVisibleTab?.type || "0";
	} finally {
		searching.value = false;
	}
}

async function loadMoreSearchResults() {
	const type = activeSearchType.value;
	const pagination = searchPaginationByType.value[type];
	if (
		!pagination ||
		pagination.finished ||
		loadingMore.value ||
		searching.value ||
		!searchMode.value
	) {
		return;
	}

	loadingMore.value = true;
	try {
		const nextPage = pagination.page + 1;
		const list = await requestSearchType(type, nextPage);
		searchResultsByType.value = {
			...searchResultsByType.value,
			[type]: [...(searchResultsByType.value[type] || []), ...list],
		};
		searchPaginationByType.value = {
			...searchPaginationByType.value,
			[type]: {
				page: nextPage,
				finished: !list.length,
			},
		};
	} catch (error) {
		console.error("加载更多搜索结果失败:", error);
		searchPaginationByType.value = {
			...searchPaginationByType.value,
			[type]: {
				...pagination,
				finished: true,
			},
		};
	} finally {
		loadingMore.value = false;
	}
}

const handleSearchResultScroll = useThrottleFn(() => {
	const container = searchResultRef.value;
	const pagination = searchPaginationByType.value[activeSearchType.value];
	if (!container || searching.value || loadingMore.value || pagination?.finished) return;

	const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
	if (distanceToBottom <= 24) {
		loadMoreSearchResults();
	}
}, 150);

async function handleSearch() {
	const value = keyword.value.trim();
	if (!value || searching.value) return;

	searchMode.value = true;
	submittedSearch.value = true;

	if (props.searchTrackingScope === "media_square") {
		const { trackMediaSearchSubmit } = useMediaSquareTracking({
			deviceType: $isMobile ? "mobile" : "pc",
			pageName: resolveMediaPageName(route.path),
		});
		await fetchAllSearchResults();
		trackMediaSearchSubmit({
			keyword: value,
			resultCount: totalResultCount.value,
		});
		track("global_search_submit", {
			keyword_length: getGlobalSearchKeywordLength(value),
			result_count: totalResultCount.value,
			has_result: totalResultCount.value > 0,
		});
		return;
	}

	await fetchAllSearchResults();
	track("global_search_submit", {
		keyword_length: getGlobalSearchKeywordLength(value),
		result_count: totalResultCount.value,
		has_result: totalResultCount.value > 0,
	});
}

async function handleHotTagClick(tag, position) {
	keyword.value = tag;
	searchMode.value = true;
	submittedSearch.value = true;
	track("global_search_hot_keyword_click", {
		keyword_tag: tag,
		position: position + 1,
	});
	await fetchAllSearchResults();
	track("global_search_submit", {
		keyword_length: getGlobalSearchKeywordLength(tag),
		result_count: totalResultCount.value,
		has_result: totalResultCount.value > 0,
	});
}

function switchSearchTab(type) {
	if (activeSearchType.value === type || searching.value) return;
	if (!(searchResultsByType.value[type] || []).length) return;
	activeSearchType.value = type;
	if (searchResultRef.value) {
		searchResultRef.value.scrollTop = 0;
	}
}

function backToRecommend() {
	searchMode.value = false;
	activeSearchType.value = "0";
	searchResultsByType.value = createEmptyResultsByType();
	searchPaginationByType.value = createPaginationByType();
	searching.value = false;
	loadingMore.value = false;
	fetchHotTeachers();
}

function clearKeyword() {
	keyword.value = "";
	backToRecommend();
	inputRef.value?.focus();
}

const SEARCH_PARENT_MASTER_FORTUNE = "/seer/master-fortune";
const SEARCH_PARENT_SQUARE = "/seer/square";

function isOnSearchParentRoute(parentPath) {
	const currentPath = route.path;
	if (currentPath === parentPath) return true;
	return currentPath.startsWith(`${parentPath}/`);
}

/** 搜索结果跳转：先把当前历史替换为列表父级，再进入详情，保证面包屑上一级正确 */
async function navigateFromSearch(targetPage, parentPath) {
	if (!targetPage) return;
	if (!isOnSearchParentRoute(parentPath)) {
		await navigateTo(parentPath, { replace: true });
	}
	await navigateTo(targetPage);
}

function handleTeacherClick(teacher) {
	if (!teacher?.link) return;
	const mentorId = teacher.answerId || teacher.uid || String(teacher.id);
	track("global_search_hot_mentor_click", {
		mentor_id: mentorId,
		mentor_type: getGlobalSearchMentorType(teacher.areaType),
		rank: teacher.rank,
		target_page: teacher.link,
	});
	closeSearchModal(GLOBAL_SEARCH_CLOSE_TYPES.NAVIGATE);
	navigateFromSearch(teacher.link, SEARCH_PARENT_MASTER_FORTUNE);
}

function handleTeacherResultClick(item, index) {
	const uid = getSearchTeacherUid(item);
	if (!uid) return;
	const targetPage = `/seer/teacher-detail/${uid}`;
	track("global_search_result_click", {
		result_type: GLOBAL_SEARCH_RESULT_TYPES.MENTOR,
		result_id: String(uid),
		position: index + 1,
		target_page: targetPage,
	});
	closeSearchModal(GLOBAL_SEARCH_CLOSE_TYPES.NAVIGATE);
	navigateFromSearch(targetPage, SEARCH_PARENT_MASTER_FORTUNE);
}

function handleServiceClick(item, index) {
	const teacherId = item?.answer_id || item?.teacher_info?.uid;
	const serviceId = item?.service_id;
	if (!teacherId || !serviceId) return;
	const targetPage = `/seer/teacher-service/${teacherId}/${serviceId}`;
	track("global_search_result_click", {
		result_type: GLOBAL_SEARCH_RESULT_TYPES.SERVICE,
		result_id: String(serviceId),
		position: index + 1,
		target_page: targetPage,
	});
	closeSearchModal(GLOBAL_SEARCH_CLOSE_TYPES.NAVIGATE);
	navigateFromSearch(targetPage, SEARCH_PARENT_MASTER_FORTUNE);
}

function handleDynamicNavigate(item, index) {
	if (!item?.id) return;
	const targetPage = `/seer/square-detail/${item.id}`;
	track("global_search_result_click", {
		result_type: GLOBAL_SEARCH_RESULT_TYPES.CONTENT,
		result_id: String(item.id),
		position: index != null ? index + 1 : undefined,
		target_page: targetPage,
	});
	closeSearchModal(GLOBAL_SEARCH_CLOSE_TYPES.NAVIGATE);
	navigateFromSearch(targetPage, SEARCH_PARENT_SQUARE);
}

function handleExpertNavigate(item, index) {
	const uid = item?.uid || item?.id;
	if (!uid) return;
	const targetPage = `/seer/creator/${uid}`;
	track("global_search_result_click", {
		result_type: GLOBAL_SEARCH_RESULT_TYPES.EXPERT,
		result_id: String(uid),
		position: index + 1,
		target_page: targetPage,
	});
	closeSearchModal(GLOBAL_SEARCH_CLOSE_TYPES.NAVIGATE);
	navigateFromSearch(targetPage, SEARCH_PARENT_SQUARE);
}

async function handleFollowExpert(item) {
	if (isItemFollowed(item) || isFollowLoading(item.uid || item.id)) return;
	if (!checkLogin()) return;

	const uid = item?.uid || item?.id;
	if (!uid) return;

	const itemKey = String(uid);
	followLoadingMap.value = { ...followLoadingMap.value, [itemKey]: true };
	const prevFollowed = isItemFollowed(item);
	item.is_follow = !prevFollowed;

	try {
		const res = await trackSquare({ uid: String(uid) });
		const result = res?.data?.[0];
		if (result) {
			item.is_follow = getFollowState(result);
		}
	} catch (error) {
		item.is_follow = prevFollowed;
		console.error("追蹤失败:", error);
		showMessage(t("square.detail.actionFailed"), "error");
	} finally {
		const next = { ...followLoadingMap.value };
		delete next[itemKey];
		followLoadingMap.value = next;
	}
}

function resetState() {
	keyword.value = "";
	searchMode.value = false;
	activeSearchType.value = "0";
	searchResultsByType.value = createEmptyResultsByType();
	searchPaginationByType.value = createPaginationByType();
	searching.value = false;
	loadingMore.value = false;
	followLoadingMap.value = {};
	keywordModified.value = false;
	submittedSearch.value = false;
	pendingCloseType.value = GLOBAL_SEARCH_CLOSE_TYPES.UNKNOWN;
	resetSession();
	resetImpressionTracker();
	inputFocusFired.value = false;
}

function handleAfterEnter() {
	track("global_search_modal_view", {
		trigger_position: resolveTriggerPosition(),
	});
	nextTick(() => {
		inputRef.value?.focus();
	});
}

function handleAfterLeave() {
	resetState();
}

watch(
	show,
	(value, oldValue) => {
		if (value && !oldValue) {
			resetSession();
			resetImpressionTracker();
			keywordModified.value = false;
			submittedSearch.value = false;
			inputFocusFired.value = false;
			pendingCloseType.value = GLOBAL_SEARCH_CLOSE_TYPES.UNKNOWN;
			// v-if 挂载时 show 一开始就是 true，after-enter 可能不触发，在这里拉热门老师
			fetchHotTeachers();
			return;
		}

		if (!value && oldValue) {
			const closeType =
				pendingCloseType.value === GLOBAL_SEARCH_CLOSE_TYPES.UNKNOWN
					? GLOBAL_SEARCH_CLOSE_TYPES.MASK
					: pendingCloseType.value;
			track("global_search_modal_close", {
				close_type: closeType,
				input_changed: keywordModified.value,
			});
		}
	},
	{ immediate: true }
);

watch(keyword, value => {
	if (show.value && String(value || "").trim()) {
		keywordModified.value = true;
	}
	if (!value.trim() && searchMode.value) {
		backToRecommend();
	}
});

onBeforeUnmount(() => {
	resetImpressionTracker();
});
</script>

<style lang="scss">
.pc-search-modal.n-modal {
	width: 768px;
	max-width: calc(100vw - 32px);
	box-shadow: none;
	background: transparent;
}

.pc-search-modal {
	.n-modal-body-wrapper {
		padding: 0;
	}
}
</style>

<style lang="scss" scoped>
.pc-search-modal__panel {
	width: 768px;
	max-width: 100%;
	height: 698px;
	max-height: calc(100vh - 80px);
	padding: 32px;
	border-radius: 20px;
	background: #ffffff;
	box-shadow: 0 12px 48px rgba(0, 0, 0, 0.12);
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.search-bar {
	position: relative;
	display: flex;
	align-items: center;
	flex-shrink: 0;
	width: 704px;
	max-width: 100%;
	height: 56px;
	margin: 0 auto;
	padding: 0 106px 0 16px;
	border-radius: 53px;
	background: #f7f7f7;
	box-sizing: border-box;
}

.search-icon {
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	margin-right: 18px;
}

.search-input {
	flex: 1;
	min-width: 0;
	height: 100%;
	border: none;
	background: transparent;
	font-size: 18px;
	color: #333333;
	outline: none;

	&::placeholder {
		color: #999999;
	}
}

.search-clear {
	display: flex;
	justify-content: center;
	align-items: center;
	position: absolute;
	right: 88px;
	top: 50%;
	transform: translateY(-50%);
	width: 24px;
	height: 24px;
	border: none;
	border-radius: 50%;
	background: #dddddd;
	color: #ffffff;
	font-size: 18px;
	line-height: 1;
	cursor: pointer;
}

.search-divider {
	position: absolute;
	right: 96px;
	top: 50%;
	transform: translateY(-50%);
	width: 1px;
	height: 28px;
	background: #e5e5e5;
}

.search-btn {
	position: absolute;
	right: 8px;
	top: 50%;
	transform: translateY(-50%);
	min-width: 72px;
	height: 40px;
	padding: 0 12px;
	border: none;
	border-radius: 20px;
	background: transparent;
	font-size: 18px;
	color: #333333;
	cursor: pointer;

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
}

.section-title {
	margin: 24px 0 16px;
	font-size: 20px;
	font-weight: 600;
	color: #333333;
	font-family: "Source Han Serif CN", serif;
}

.hot-section {
	flex-shrink: 0;
}

.teacher-section,
.search-result-section {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
}

.search-tabs {
	display: flex;
	gap: 12px;
	margin: 20px 0;
	flex-shrink: 0;
}

.search-tab {
	min-width: 88px;
	height: 40px;
	padding: 0 20px;
	border: 1px solid #e8e8e8;
	border-radius: 20px;
	background: #ffffff;
	font-size: 16px;
	color: #666666;
	cursor: pointer;
	transition:
		border-color 0.2s ease,
		color 0.2s ease;

	&--active {
		border-color: #333333;
		color: #333333;
		font-weight: 600;
	}
}

.hot-tags {
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
}

.hot-tag {
	min-width: 88px;
	height: 40px;
	padding: 0 20px;
	border: 1px solid #e8e8e8;
	border-radius: 20px;
	background: #ffffff;
	font-size: 16px;
	color: #333333;
	cursor: pointer;
	transition: border-color 0.2s ease;

	&:hover {
		border-color: #e6a73a;
		color: #e6a73a;
	}
}

.teacher-list {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 12px 16px;
	margin: 0;
	padding: 0;
	list-style: none;
}

.teacher-item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	border: 1px solid #ececec;
	border-radius: 12px;
	background: #ffffff;
	cursor: pointer;
	transition: border-color 0.2s ease;

	&:hover {
		border-color: #e6d4b0;
	}
}

.teacher-rank {
	flex-shrink: 0;
	width: 28px;
	font-size: 28px;
	line-height: 1;
	font-weight: 700;
	color: #333333;
	font-family: "Source Han Serif CN", serif;
	text-align: center;
}

.teacher-avatar {
	flex-shrink: 0;
	width: 64px;
	height: 64px;
	border-radius: 50%;
	border: 1px solid #eeeeee;
	object-fit: cover;
	background: #f5f5f5;
}

.teacher-info {
	min-width: 0;
	flex: 1;
}

.teacher-meta-row {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
}

.teacher-name {
	flex-shrink: 1;
	min-width: 0;
	font-size: 15px;
	font-weight: 600;
	color: #333333;
	line-height: 1.4;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	font-family:
		PingFang SC,
		sans-serif;
}

.teacher-rating {
	display: inline-flex;
	align-items: center;
	flex-shrink: 0;
	gap: 4px;
	height: 22px;
	padding: 0 8px;
	border-radius: 4px;
	background: #fff6e5;
	font-size: 12px;
	line-height: 1;
	color: #666666;
	white-space: nowrap;

	img {
		width: 12px;
		height: 12px;
		flex-shrink: 0;
	}
}

.teacher-tags {
	margin-top: 6px;
	font-size: 13px;
	color: #999999;
	line-height: 1.4;
	font-family:
		PingFang SC,
		sans-serif;
}

.result-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin: 0;
	padding: 0;
	list-style: none;
}

.result-item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px;
	border: 1px solid #f0f0f0;
	cursor: pointer;
	border-radius: 13px;

	&--expert {
		cursor: pointer;
	}

	&__avatar {
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		border-radius: 50%;
		object-fit: cover;
		background: #f5f5f5;
	}

	&__cover {
		flex-shrink: 0;
		width: 72px;
		height: 72px;
		border-radius: 8px;
		object-fit: cover;
		background: #f5f5f5;
	}

	&__content {
		min-width: 0;
		flex: 1;
	}

	&__title {
		font-size: 15px;
		font-weight: 600;
		color: #333333;
		line-height: 1.4;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__desc {
		margin-top: 4px;
		font-size: 13px;
		color: #999999;
		line-height: 1.5;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		overflow: hidden;
	}

	&__meta {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		margin-top: 6px;
		font-size: 13px;
		color: #999999;
	}

	&__price {
		color: #e6a73a;
		font-weight: 600;
	}
}

.result-tag {
	display: inline-flex;
	margin-top: 6px;
	padding: 2px 8px;
	border-radius: 4px;
	background: #fff1f0;
	font-size: 12px;
	color: #ff4d4f;
}

.follow-btn {
	flex-shrink: 0;
	min-width: 88px;
	height: 36px;
	padding: 0 16px;
	border: none;
	border-radius: 8px;
	background: #333333;
	font-size: 14px;
	color: #ffffff;
	cursor: pointer;

	&--followed {
		background: #f0f0f0;
		color: #999999;
	}
}

.state-text {
	padding: 24px 0 8px;
	font-size: 16px;
	color: #999999;
	text-align: center;
}

.search-result-section__footer {
	padding: 16px 0 8px;
	font-size: 14px;
	color: #999999;
	text-align: center;
}
</style>
